/**
 * GreetingBar - who you are and what day it is, and nothing else.
 *
 * It used to carry a focus line too ("3 tasks are overdue"), which made it the
 * tallest thing on the page and buried the one ACTIONABLE sentence on the
 * dashboard inside a decorative hero band. The focus moved to `NeedsAttention`,
 * which is a card of clickable rows sitting beside "At a glance" - where an
 * action belongs - and this went back to being what a greeting is: two lines,
 * quiet, out of the way.
 *
 * The champagne hairline down the left edge is the only accent: this is the
 * page's opening statement, and the accent marks it as such. Champagne is for
 * emphasis at that scale, never for filling a surface.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, RootState } from '../../../store';
import { getIntlLocale } from '../../../utils/functions';

export interface GreetingBarProps {
  /** Unused - kept so an older caller does not break. See the note above. */
  tasks?: unknown[];
  events?: unknown[];
}

const GreetingBar: React.FC<GreetingBarProps> = () => {
  const { t, i18n } = useTranslation();

  const userInfo = useAppSelector((state: RootState) => state.user.userInfo);
  const firstName = userInfo?.first_name || userInfo?.name?.split(' ')[0] || 'there';

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('Good morning');
    if (hour < 17) return t('Good afternoon');
    return t('Good evening');
  }, [t]);

  const dateStr = useMemo(() => {
    return new Intl.DateTimeFormat(getIntlLocale(), {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className="glass relative flex h-full flex-col justify-center overflow-hidden rounded-lg px-5 py-4 shadow-sm">
      <span aria-hidden className="absolute inset-y-3 start-0 w-0.5 rounded-full bg-champagne" />
      <h1 className="truncate text-lg text-fg-strong">
        {greeting}, {firstName}
      </h1>
      <p className="mt-0.5 text-xs text-fg-muted">{dateStr}</p>
    </div>
  );
};

export default GreetingBar;
